import { useState, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import JublyLogo from '../components/JublyLogo';
import { signup as apiSignup, verifyRecoveryEmail, resendVerification, checkUsername, checkDomain, purchaseDomain } from '../services/emailApi';

const SIGNUP_SOURCES = [
  { value: '', label: 'Select...' },
  { value: 'friend_referral', label: 'A friend or colleague' },
  { value: 'search_engine', label: 'Google / search engine' },
  { value: 'social_media', label: 'Social media (Twitter, LinkedIn, etc.)' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'blog_article', label: 'Blog or article' },
  { value: 'app_store', label: 'App store' },
  { value: 'other', label: 'Other' },
];

export default function SignupPage() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Capture referral and UTM params from URL
  const refCode = searchParams.get('ref') || '';
  const utmParams = useMemo(() => ({
    utmSource: searchParams.get('utm_source') || '',
    utmMedium: searchParams.get('utm_medium') || '',
    utmCampaign: searchParams.get('utm_campaign') || '',
  }), [searchParams]);

  // Shared state
  const [signupType, setSignupType] = useState('personal');
  const [step, setStep] = useState('form');
  // Steps: form | domain-select | domain-input | domain-search | admin-form | verify | dns-setup
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Account fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(null);
  const [usernameTimer, setUsernameTimer] = useState(null);

  // Verification
  const [code, setCode] = useState('');
  const [resending, setResending] = useState(false);

  // Signup attribution
  const [signupSource, setSignupSource] = useState(refCode ? 'friend_referral' : '');

  // Business domain
  const [domainOption, setDomainOption] = useState(null); // 'have', 'need'
  const [domain, setDomain] = useState('');
  const [domainSearch, setDomainSearch] = useState('');
  const [domainStatus, setDomainStatus] = useState(null);
  const [domainPricing, setDomainPricing] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [dnsMethod, setDnsMethod] = useState(null); // 'nameservers', 'records'

  const isPersonal = signupType === 'personal';
  const weOwnDomain = domainOption === 'need'; // purchased through us
  const emailDomain = isPersonal ? 'jubly.me' : domain;
  const cleanUsername = username.toLowerCase().replace(/[^a-z0-9.-]/g, '');
  const fullEmail = cleanUsername && emailDomain ? `${cleanUsername}@${emailDomain}` : '';

  // ── Username check ──
  function handleUsernameChange(value) {
    const clean = value.toLowerCase().replace(/[^a-z0-9.-]/g, '');
    setUsername(clean);
    setUsernameStatus(null);
    setError('');
    if (usernameTimer) clearTimeout(usernameTimer);
    if (clean.length < 3) { if (clean.length > 0) setUsernameStatus('invalid'); return; }
    setUsernameStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const resp = await checkUsername(clean, isPersonal ? undefined : domain);
        setUsernameStatus(resp?.available ? 'available' : 'taken');
      } catch (err) {
        setUsernameStatus(err.response?.status === 409 ? 'taken' : 'available');
      }
    }, 500);
    setUsernameTimer(timer);
  }

  // ── "I have a domain" → go to admin form ──
  function handleHaveDomain(e) {
    e?.preventDefault();
    if (!domain.includes('.')) return;
    setDomain(domain.toLowerCase().trim());
    setStep('admin-form');
  }

  // ── Domain search ──
  async function handleSearchDomain(e) {
    e?.preventDefault();
    if (!domainSearch.includes('.')) return;
    setDomainStatus('checking');
    setError('');
    try {
      const result = await checkDomain(domainSearch);
      if (result.available) {
        setDomainStatus('available');
        setDomainPricing(result);
        setDomain(domainSearch);
      } else {
        setDomainStatus('unavailable');
        setDomainPricing(null);
      }
    } catch (err) {
      setDomainStatus('error');
      setError(err.response?.data?.error || 'Domain check failed');
    }
  }

  // ── Domain purchase → admin form ──
  async function handlePurchaseDomain() {
    setPurchasing(true);
    setError('');
    try {
      await purchaseDomain(domain);
      setDomainStatus('purchased');
      setStep('admin-form');
    } catch (err) {
      setError(err.response?.data?.error || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  }

  // ── Redirect if authenticated (unless in post-signup steps) ──
  if (isAuthenticated && !['verify', 'dns-setup'].includes(step)) {
    navigate('/email', { replace: true });
    return null;
  }

  // ── Signup ──
  async function handleSignup(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await apiSignup({
        email: fullEmail,
        password,
        displayName: displayName.trim() || cleanUsername,
        recoveryEmail: recoveryEmail.trim(),
        domain: isPersonal ? undefined : domain,
        ref: refCode || undefined,
        signupSource: signupSource || undefined,
        ...utmParams,
      });
      await login(fullEmail, password);
      setStep('verify');
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed');
    } finally {
      setLoading(false);
    }
  }

  // ── Verify → then DNS setup (for "have domain") or done ──
  async function handleVerify(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyRecoveryEmail(code.trim());
      if (!isPersonal && !weOwnDomain) {
        setStep('dns-setup');
      } else {
        navigate('/email');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResending(true);
    try { await resendVerification(); setError(''); }
    catch (err) { setError(err.response?.data?.error || 'Failed to resend'); }
    finally { setResending(false); }
  }

  function switchType(type) {
    setSignupType(type);
    setStep(type === 'personal' ? 'form' : 'domain-select');
    setError('');
    setDomainOption(null);
    setDomain('');
    setDomainStatus(null);
    setUsername('');
    setUsernameStatus(null);
    setAgreedToTerms(false);
  }

  const canSubmitAccount = cleanUsername.length >= 3
    && password.length >= 8
    && password === confirmPassword
    && recoveryEmail.trim()
    && agreedToTerms
    && usernameStatus !== 'taken'
    && usernameStatus !== 'checking';

  // ── Shared account form fields ──
  const accountFields = (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {isPersonal ? 'Choose your email' : `Admin email for ${domain}`}
        </label>
        <div className="flex items-center rounded-lg border border-gray-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20 overflow-hidden">
          <input type="text" required autoFocus autoComplete="username" value={username}
            onChange={(e) => handleUsernameChange(e.target.value)} placeholder="yourname"
            className="flex-1 border-none px-4 py-3 text-sm focus:outline-none focus:ring-0" />
          <span className="bg-gray-50 border-l border-gray-300 px-3 py-3 text-sm text-gray-500 font-medium select-none whitespace-nowrap">
            @{emailDomain}
          </span>
        </div>
        {usernameStatus === 'checking' && <p className="mt-1 text-xs text-gray-400">Checking availability...</p>}
        {usernameStatus === 'available' && <p className="mt-1 text-xs text-green-600">{cleanUsername}@{emailDomain} is available</p>}
        {usernameStatus === 'taken' && <p className="mt-1 text-xs text-red-600">{cleanUsername}@{emailDomain} is already taken</p>}
        {usernameStatus === 'invalid' && <p className="mt-1 text-xs text-amber-600">Username must be at least 3 characters</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Password <span className="text-red-500">*</span></label>
        <input type="password" required minLength={8} autoComplete="new-password" value={password}
          onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters"
          className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm password <span className="text-red-500">*</span></label>
        <input type="password" required minLength={8} autoComplete="new-password" value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter password"
          className={`w-full rounded-lg border px-4 py-3 text-sm focus:ring-2 ${
            confirmPassword && confirmPassword !== password
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
          }`} />
        {confirmPassword && confirmPassword !== password && <p className="mt-1 text-xs text-red-600">Passwords don't match</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Display name</label>
        <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
          placeholder="First Last"
          className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Recovery email <span className="text-red-500">*</span></label>
        <input type="email" required value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)}
          placeholder="personal@gmail.com"
          className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
        <p className="mt-1 text-xs text-gray-500">Used for account recovery. Must be a different email you control.</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">How did you hear about Jubly?</label>
        <select value={signupSource} onChange={(e) => setSignupSource(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20">
          {SIGNUP_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      {refCode && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          Referred by a friend — you both earn contribution points!
        </div>
      )}
      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
        <span className="text-xs text-gray-600">
          I agree to the{' '}
          <Link to="/terms" className="text-blue-600 hover:text-blue-800">Terms of Service</Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-blue-600 hover:text-blue-800">Privacy Policy</Link>
        </span>
      </label>
    </>
  );

  // ── Page titles ──
  const titles = {
    form: 'Create your Jubly account',
    'domain-select': 'Create your Jubly account',
    'domain-input': 'Create your Jubly account',
    'domain-search': 'Create your Jubly account',
    'admin-form': `Create admin account for ${domain}`,
    verify: 'Verify your email',
    'dns-setup': `Connect ${domain}`,
  };
  const subtitles = {
    form: 'Email, Calendar & Voice for you or your team',
    'domain-select': 'Email, Calendar & Voice for you or your team',
    'domain-input': 'Email, Calendar & Voice for you or your team',
    'domain-search': 'Email, Calendar & Voice for you or your team',
    'admin-form': `Set up the first account on ${domain}`,
    verify: `We sent a 6-digit code to ${recoveryEmail}`,
    'dns-setup': 'Choose how to connect your domain',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <nav className="flex items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-2">
          <JublyLogo size={24} />
          <span className="font-bold text-gray-900 text-sm">Jubly</span>
        </Link>
        <Link to="/login" className="text-sm text-blue-600 hover:text-blue-800 font-medium">Sign in</Link>
      </nav>
      <div className="flex items-center justify-center p-4" style={{ minHeight: 'calc(100vh - 64px)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4">
            <JublyLogo size={64} className="mx-auto shadow-lg rounded-2xl" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{titles[step] || titles.form}</h1>
          <p className="mt-2 text-sm text-gray-600">{subtitles[step] || subtitles.form}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">

          {/* ── Tab bar ── */}
          {['form', 'domain-select', 'domain-input', 'domain-search'].includes(step) && (
            <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
              <button onClick={() => switchType('personal')}
                className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
                  isPersonal ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}>For me</button>
              <button onClick={() => switchType('business')}
                className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
                  !isPersonal ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}>For my business</button>
            </div>
          )}

          {/* ── Personal signup ── */}
          {step === 'form' && isPersonal && (
            <form onSubmit={handleSignup} className="space-y-4">
              {accountFields}
              {error && <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}
              <button type="submit" disabled={loading || !canSubmitAccount}
                className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">
                {loading ? 'Creating account...' : 'Create account'}
              </button>
            </form>
          )}

          {/* ── Business: choose domain path ── */}
          {step === 'domain-select' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center mb-2">How would you like to set up your business?</p>
              <button onClick={() => { setDomainOption('have'); setStep('domain-input'); setDomain(''); }}
                className="w-full rounded-lg border-2 border-gray-200 p-4 text-left transition-colors hover:border-blue-400 hover:bg-blue-50">
                <p className="font-semibold text-gray-900 text-sm">I have a domain</p>
                <p className="text-xs text-gray-500 mt-1">Connect your existing domain (e.g. mycompany.com)</p>
              </button>
              <button onClick={() => { setDomainOption('need'); setStep('domain-search'); setDomainSearch(''); }}
                className="w-full rounded-lg border-2 border-gray-200 p-4 text-left transition-colors hover:border-blue-400 hover:bg-blue-50">
                <p className="font-semibold text-gray-900 text-sm">I need a domain</p>
                <p className="text-xs text-gray-500 mt-1">Search and purchase a new domain for your business</p>
              </button>
            </div>
          )}

          {/* ── Business: enter existing domain → straight to admin form ── */}
          {step === 'domain-input' && (
            <form onSubmit={handleHaveDomain} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Your domain</label>
                <input type="text" required autoFocus value={domain}
                  onChange={(e) => setDomain(e.target.value.toLowerCase().trim())}
                  placeholder="mycompany.com"
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
                <p className="mt-1 text-xs text-gray-500">You'll configure DNS after creating your account.</p>
              </div>
              {error && <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}
              <button type="submit" disabled={!domain.includes('.')}
                className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">
                Continue
              </button>
              <button type="button" onClick={() => setStep('domain-select')}
                className="w-full text-sm text-gray-500 hover:text-gray-700">Back</button>
            </form>
          )}

          {/* ── Business: search/purchase domain ── */}
          {step === 'domain-search' && (
            <div className="space-y-4">
              <form onSubmit={handleSearchDomain}>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Search for a domain</label>
                <div className="flex gap-2">
                  <input type="text" required autoFocus value={domainSearch}
                    onChange={(e) => { setDomainSearch(e.target.value.toLowerCase().trim()); setDomainStatus(null); }}
                    placeholder="mycompany.com"
                    className="flex-1 rounded-lg border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
                  <button type="submit" disabled={!domainSearch.includes('.') || domainStatus === 'checking'}
                    className="rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
                    {domainStatus === 'checking' ? '...' : 'Search'}
                  </button>
                </div>
              </form>
              {domainStatus === 'available' && domainPricing && (
                <div className="rounded-lg border-2 border-green-200 bg-green-50 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-green-800 text-sm">{domain} is available!</p>
                      {domainPricing.price && <p className="text-xs text-green-600 mt-0.5">${domainPricing.price}/year</p>}
                    </div>
                    <button onClick={handlePurchaseDomain} disabled={purchasing}
                      className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50 transition-colors">
                      {purchasing ? 'Purchasing...' : 'Purchase'}
                    </button>
                  </div>
                </div>
              )}
              {domainStatus === 'unavailable' && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                  <p className="text-sm text-red-700">{domainSearch} is not available. Try a different domain.</p>
                </div>
              )}
              {error && <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}
              <button type="button" onClick={() => setStep('domain-select')}
                className="w-full text-sm text-gray-500 hover:text-gray-700">Back</button>
            </div>
          )}

          {/* ── Business: admin account form ── */}
          {step === 'admin-form' && (
            <form onSubmit={handleSignup} className="space-y-4">
              {accountFields}
              {error && <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}
              <button type="submit" disabled={loading || !canSubmitAccount}
                className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">
                {loading ? 'Creating admin account...' : 'Create admin account'}
              </button>
              <button type="button" onClick={() => { setStep(weOwnDomain ? 'domain-search' : 'domain-input'); setUsername(''); setUsernameStatus(null); }}
                className="w-full text-sm text-gray-500 hover:text-gray-700">Back</button>
            </form>
          )}

          {/* ── Verify email ── */}
          {step === 'verify' && (
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Verification code</label>
                <input type="text" required autoFocus maxLength={6} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 text-center text-2xl font-mono tracking-[0.5em] focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
              </div>
              {error && <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}
              <button type="submit" disabled={loading || code.length !== 6}
                className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">
                {loading ? 'Verifying...' : 'Verify'}
              </button>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={handleResend} disabled={resending}
                  className="text-blue-600 hover:text-blue-800 disabled:opacity-50">
                  {resending ? 'Sending...' : 'Resend code'}
                </button>
                <button type="button" onClick={() => !isPersonal && !weOwnDomain ? setStep('dns-setup') : navigate('/email')}
                  className="text-gray-500 hover:text-gray-700">Skip for now</button>
              </div>
            </form>
          )}

          {/* ── DNS setup (after signup, for "I have a domain" only) ── */}
          {step === 'dns-setup' && (
            <div className="space-y-5">
              <p className="text-sm text-gray-600">Choose how you'd like to connect <strong>{domain}</strong> to Jubly:</p>

              {/* Option 1: Nameservers */}
              <button onClick={() => setDnsMethod(dnsMethod === 'nameservers' ? null : 'nameservers')}
                className={`w-full rounded-lg border-2 p-4 text-left transition-colors ${
                  dnsMethod === 'nameservers' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                }`}>
                <p className="font-semibold text-gray-900 text-sm">Change nameservers</p>
                <p className="text-xs text-gray-500 mt-1">Manage your entire domain with Jubly (email, calendar, voice, website)</p>
              </button>

              {dnsMethod === 'nameservers' && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
                  <p className="text-sm text-gray-700">At your domain registrar, change the nameservers for <strong>{domain}</strong> to:</p>
                  <div className="space-y-1.5 font-mono text-sm">
                    <div className="bg-white rounded-lg px-3 py-2 border border-blue-200 text-gray-800 select-all">ryan.ns.cloudflare.com</div>
                    <div className="bg-white rounded-lg px-3 py-2 border border-blue-200 text-gray-800 select-all">wally.ns.cloudflare.com</div>
                  </div>
                  <p className="text-xs text-gray-500">We'll automatically configure email, calendar, and voice subdomains once the nameservers propagate (up to 48 hours).</p>
                </div>
              )}

              {/* Option 2: DNS records */}
              <button onClick={() => setDnsMethod(dnsMethod === 'records' ? null : 'records')}
                className={`w-full rounded-lg border-2 p-4 text-left transition-colors ${
                  dnsMethod === 'records' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                }`}>
                <p className="font-semibold text-gray-900 text-sm">Add DNS records</p>
                <p className="text-xs text-gray-500 mt-1">Keep your current DNS provider and add Jubly email alongside your existing setup</p>
              </button>

              {dnsMethod === 'records' && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-4">
                  <p className="text-sm text-gray-700">Add these records at your DNS provider:</p>

                  {/* CNAME records */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-bold text-white bg-purple-500 rounded px-1.5 py-0.5">CNAME</span>
                      <span className="text-xs text-gray-500">Service subdomains</span>
                    </div>
                    <div className="overflow-x-auto bg-white rounded-lg border border-blue-200">
                      <table className="w-full text-xs font-mono">
                        <thead><tr className="text-gray-500 border-b border-blue-100"><th className="text-left py-1.5 px-2">Name</th><th className="text-left py-1.5 px-2">Value</th></tr></thead>
                        <tbody className="text-gray-700">
                          <tr className="border-b border-blue-50"><td className="py-1.5 px-2 select-all">mail.{domain}</td><td className="py-1.5 px-2 select-all">jubly.net</td></tr>
                          <tr className="border-b border-blue-50"><td className="py-1.5 px-2 select-all">calendar.{domain}</td><td className="py-1.5 px-2 select-all">jubly.net</td></tr>
                          <tr><td className="py-1.5 px-2 select-all">voice.{domain}</td><td className="py-1.5 px-2 select-all">jubly.net</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* MX records */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-bold text-white bg-amber-500 rounded px-1.5 py-0.5">MX</span>
                      <span className="text-xs text-gray-500">Email routing (set on {domain})</span>
                    </div>
                    <div className="overflow-x-auto bg-white rounded-lg border border-blue-200">
                      <table className="w-full text-xs font-mono">
                        <thead><tr className="text-gray-500 border-b border-blue-100"><th className="text-left py-1.5 px-2">Priority</th><th className="text-left py-1.5 px-2">Value</th></tr></thead>
                        <tbody className="text-gray-700">
                          <tr className="border-b border-blue-50"><td className="py-1.5 px-2">10</td><td className="py-1.5 px-2 select-all">route1.mx.cloudflare.net</td></tr>
                          <tr className="border-b border-blue-50"><td className="py-1.5 px-2">20</td><td className="py-1.5 px-2 select-all">route2.mx.cloudflare.net</td></tr>
                          <tr><td className="py-1.5 px-2">30</td><td className="py-1.5 px-2 select-all">route3.mx.cloudflare.net</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* TXT records */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-bold text-white bg-green-500 rounded px-1.5 py-0.5">TXT</span>
                      <span className="text-xs text-gray-500">Email security</span>
                    </div>
                    <div className="space-y-2 text-xs font-mono">
                      <div className="bg-white rounded-lg border border-blue-200 px-2 py-1.5">
                        <span className="text-gray-400">SPF (on {domain}):</span>
                        <div className="text-gray-700 select-all break-all mt-0.5">v=spf1 include:_spf.mx.cloudflare.net ~all</div>
                      </div>
                      <div className="bg-white rounded-lg border border-blue-200 px-2 py-1.5">
                        <span className="text-gray-400">DMARC (on _dmarc.{domain}):</span>
                        <div className="text-gray-700 select-all break-all mt-0.5">v=DMARC1; p=quarantine; rua=mailto:dmarc@{domain}</div>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500">DNS changes can take up to 48 hours to propagate.</p>
                </div>
              )}

              <button onClick={() => navigate('/email')}
                className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition-colors shadow-sm">
                {dnsMethod ? "I've updated my DNS — go to my inbox" : 'Skip for now — go to my inbox'}
              </button>
            </div>
          )}

          <p className="mt-6 text-center text-xs text-gray-500">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-800 font-medium">Sign in</Link>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}
